/**
 * Pure transform: validated JSON → (ImportedCity record, Plan record) ready
 * to be written to IndexedDB. All FoE data quirks live here so the storage
 * layer never has to know about them. See docs/data-formats.md.
 */
import type { GridRect, InventoryItem, PlanBuilding } from '$lib/types'
import { resolveBuilding } from '$lib/catalogue'
import { validateImportText, type ImportValidation } from './validation'

export interface ParseSuccess {
  ok: true
  importedCity: {
    name: string
    importedAt: number
    rawJson: string
    parsed: {
      cityMapData: Record<string, unknown>
      unlockedAreas: GridRect[]
      cityEntities: Record<string, unknown>
    }
  }
  initialPlan: {
    name: string
    createdAt: number
    updatedAt: number
    unlockedAreas: GridRect[]
    buildings: PlanBuilding[]
    inventory: InventoryItem[]
    notes: string
    nextEntityId: number
    version: number
  }
  /** Counts surfaced for diagnostics + the dialog's success message. */
  stats: {
    totalEntries: number
    placedBuildings: number
    offGridSkipped: number
    unknownEntities: number
  }
}

export interface ParseFailure {
  ok: false
  message: string
  code: ImportValidation extends infer V
    ? V extends { ok: false; code: infer C }
      ? C
      : never
    : never
}

export type ParseResult = ParseSuccess | ParseFailure

interface RawPlacement {
  id?: number
  cityentity_id?: string
  type?: string
  x?: number
  y?: number
  level?: number
}

interface RawUnlocked {
  x?: number
  y?: number
  width?: number
  length?: number
}

/**
 * Default the player-name-extract to whatever PlayerName / player.name we
 * can find. The export has no canonical place; we look in a few common spots
 * and fall back to "Untitled City".
 */
function extractName(json: Record<string, unknown>): string {
  const candidates: unknown[] = [
    (json.PlayerName as unknown) ?? null,
    (json.Player as { name?: unknown } | undefined)?.name,
    (json.City as { name?: unknown } | undefined)?.name,
  ]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim()
  }
  return 'Untitled City'
}

function normaliseUnlocked(raw: unknown[]): GridRect[] {
  return raw.map((entry) => {
    const r = (entry ?? {}) as RawUnlocked
    // x/y default to 0 — see docs/data-formats.md "x and y default to 0".
    return {
      x: typeof r.x === 'number' ? r.x : 0,
      y: typeof r.y === 'number' ? r.y : 0,
      width: typeof r.width === 'number' ? r.width : 0,
      length: typeof r.length === 'number' ? r.length : 0,
    }
  })
}

export function parseImport(rawText: string, now: number = Date.now()): ParseResult {
  const validation = validateImportText(rawText)
  if (!validation.ok) {
    return { ok: false, code: validation.code, message: validation.message }
  }

  const { json, raw: rawObj } = validation
  const cityEntitiesRecord = json.CityEntities

  const placements = json.CityMapData
  const placementIds = Object.keys(placements)

  const unlockedAreas = normaliseUnlocked(json.UnlockedAreas)

  const buildings: PlanBuilding[] = []
  let nextSyntheticId = 0
  let offGridSkipped = 0
  let unknownEntities = 0

  // Build a synthetic ImportedCity-like object so we can call resolveBuilding
  // with the import's CityEntities as the priority source.
  const importLens = {
    id: -1, // not yet persisted; resolver only uses id for caching, fine to be transient
    name: '',
    importedAt: now,
    rawJson: '',
    parsed: {
      cityMapData: placements,
      unlockedAreas,
      cityEntities: cityEntitiesRecord,
    },
  }

  for (const key of placementIds) {
    const p = (placements[key] ?? {}) as RawPlacement
    const cityentityId = typeof p.cityentity_id === 'string' ? p.cityentity_id : null
    if (!cityentityId) continue

    const resolved = resolveBuilding(cityentityId, importLens)
    if (!resolved) {
      unknownEntities += 1
      // Unknown entities still get placed — the renderer draws them as a
      // grey hatched rectangle. See docs/architecture.md "Handling unknown
      // entities". We can't know their footprint though, so width/height
      // come from whatever the placement implies (assume 1×1 if missing).
    } else if (resolved.isOffGrid) {
      offGridSkipped += 1
      continue
    }

    const numericId =
      typeof p.id === 'number' && Number.isFinite(p.id)
        ? p.id
        : -1 - nextSyntheticId++

    const building: PlanBuilding = {
      id: numericId,
      cityentityId,
      // x/y default to 0 — see docs/data-formats.md.
      x: typeof p.x === 'number' ? p.x : 0,
      y: typeof p.y === 'number' ? p.y : 0,
    }

    if (resolved?.type === 'greatbuilding' && typeof p.level === 'number') {
      building.level = p.level
    }

    buildings.push(building)
  }

  const name = extractName(rawObj)

  // Counter starts one above the largest positive id present, so the editor
  // can vend new ids that won't clash with anything FoE already gave us.
  // (Synthetic negative ids never reach this max — they're always < 1.)
  let maxId = 0
  for (const b of buildings) {
    if (b.id > maxId) maxId = b.id
  }
  const nextEntityId = maxId + 1

  return {
    ok: true,
    importedCity: {
      name,
      importedAt: now,
      rawJson: rawText,
      parsed: {
        cityMapData: placements,
        unlockedAreas,
        cityEntities: cityEntitiesRecord,
      },
    },
    initialPlan: {
      name,
      createdAt: now,
      updatedAt: now,
      unlockedAreas,
      buildings,
      inventory: [],
      notes: '',
      nextEntityId,
      version: 0,
    },
    stats: {
      totalEntries: placementIds.length,
      placedBuildings: buildings.length,
      offGridSkipped,
      unknownEntities,
    },
  }
}
